import { ReactElement, ReactNode, useCallback, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { Box, ClickAwayListener, Divider, Grid, Grow, MenuItem, MenuList, Paper, Popper, Skeleton } from "@mui/material"
import { enqueueSnackbar } from "notistack"
import { makeStyles } from "tss-react/mui"

import { useApiMutation } from "../../../../../../../core/api/useApiMutation"
import { useApiQuery } from "../../../../../../../core/api/useApiQuery"
import { useAppSelector } from "../../../../../../store/storeHooks"
import { selectEffectiveCompanyId, selectEffectiveUserId } from "../../../../../../context/contextSelectors"
import { LocalizedEnum, LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../../../localization/useDateTime"
import { tailwindPalette } from "../../../../../../theme/tailwindPalette"
import { CalendarEvent } from "../../../../../../../shared/components/calendar/calendarTypes"
import { ConfigErrorBoundary } from "../../../../../../../shared/components/createOrder/components/ConfigErrorBoundary"
import { CreateOrderDialogMode } from "../../../../../../../shared/components/createOrder/createOrderTypes"
import { CreateOrderType } from "../../../../../../../shared/components/createOrder/helpers"
import { useOrderDialogUrlState } from "../../../../../../../shared/components/createOrder/hooks/useOrderDialogUrlState"
import { ConfirmModal } from "../../../../../../../shared/components/dialogs/ConfirmModal"
import { StandardDialog } from "../../../../../../../shared/components/dialogs/StandardDialog"
import { LockedDialog, useLockedDialogState } from "../../../../../../../shared/components/dialogs/LockedDialog"
import {
    StandardErrorDialog,
    useStandardErrorDialogState,
} from "../../../../../../../shared/components/dialogs/StandardErrorDialog"
import {
    StandardSuccessDialog,
    SuccessDialogType,
} from "../../../../../../../shared/components/dialogs/StandardSuccessDialog"
import { MeetingServiceOrder } from "../../../../../../merchant/merchant/production/meetingService/meetingServiceTypes"
import { UserDetails } from "../../../../../nonAdminUserApi"
import { UpcomingOrdersMeetingService } from "../../../../upcomingOrders/meetingService/meetingServiceApi"
import { ServiceCenterHomeQuery } from "../../../../../../serviceCenter/report/home/serviceCenterHomeApi"
import { ServiceCenterUpcomingMeetingsQuery } from "../../../../../../serviceCenter/report/upcomingBookings/upcomingMeetingsApi"
import { MenuMeeting } from "../../../weekMenuTypes"
import { UserWeekMenu } from "../../../weekMenuApi"
import {
    fetchOrderDetails,
    MeetingOrderAction,
    OrderDetailsResponse,
    ordersEndBooking,
} from "../../../meetingServiceApi"
import { UserMeetingsReceipt } from "../../../../MeetingsReceipt/UserMeetingsReceipt"

import { ActionConfirmationDialog } from "./ActionConfirmationDialog"
import { ExtendBookingContent } from "./ExtendBookingContent"
import { UserDeleteMeetingDialog } from "./UserDeleteMeetingDialog"

type StyleProps = {
    hasTopPadding: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, { hasTopPadding }) => ({
    menu: {
        "& .MuiPaper-root": {
            padding: 0,
            minWidth: "10rem",
            width: "max-content",
            "& li:last-child": {
                marginBottom: "0.5rem",
            },
        },
        "& .MuiList-root": {
            padding: 0,
            paddingTop: hasTopPadding ? "0.5rem" : 0,
        },
        "& .MuiMenuItem-root": {
            whiteSpace: "normal",
            wordWrap: "break-word",
        },
    },
    updateModalContainer: {
        padding: "0 2rem 2rem 2rem",
    },
    deleteItem: {
        color: tailwindPalette.red[600],
    },
    skeletonCard: {
        height: "1.5rem",
        minWidth: "14rem",
        margin: ".5rem",
        borderRadius: ".625rem",
    },
    topComponentDivider: {
        marginBottom: "0.5rem",
    },
    hideEdge: { display: "none" },
    noActionsMenuItem: {
        padding: "0.5rem 1rem",
        whiteSpace: "nowrap",
    },
    popper: {
        zIndex: 1300,
        "@media print": {
            display: "none",
        },
    },
}))

const transformData = (data: OrderDetailsResponse) => (data?.orders.length ? data?.orders[0] : undefined)
export type ActionType = "edit_meeting" | "duplicate_meeting_service"
type MenuVariant = "meeting-service" | "resource-booking"
type MenuActionKey = keyof MeetingOrderAction | "showReceipt" | "duplicateMeetingService" | "editMeeting"
export type MeetingCardOrderTypes = CalendarEvent

type MenuItemConfig = {
    key: MenuActionKey
    element: ReactElement
    dividerBefore?: boolean
}

export type AdditionalEventMenuItem = {
    label: string
    key: string
    onClick: (id: number) => void
}

type MeetingMenuCardProps = {
    ignoreMenuContainer?: boolean
    anchorEl?: HTMLElement | null
    handleMenuClose: () => void
    menuOnApi: boolean
    topComponent?: ReactNode
    meeting: MenuMeeting
    meetings?: MenuMeeting[] | MeetingServiceOrder[]
    variant?: MenuVariant
    additionalMenuItems?: AdditionalEventMenuItem[]
    orders?: MeetingCardOrderTypes[]
    onChatOpen?: (orderId: number, orderTitle: string) => void
    onMenuMouseEnter?: () => void
    onMenuMouseLeave?: () => void
    onOrderDeleted?: () => void
}

export function MeetingCard({
    ignoreMenuContainer = false,
    anchorEl,
    handleMenuClose,
    meeting,
    meetings,
    menuOnApi,
    topComponent,
    additionalMenuItems,
    variant = "resource-booking",
    orders,
    onChatOpen,
    onMenuMouseEnter,
    onMenuMouseLeave,
    onOrderDeleted,
}: MeetingMenuCardProps) {
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const { openDialog } = useOrderDialogUrlState()
    const queryClient = useQueryClient()
    const userId = useAppSelector(selectEffectiveUserId)
    const companyId = useAppSelector(selectEffectiveCompanyId)

    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const { errorDialog: showErrorDialog, openErrorDialog, closeErrorDialog } = useStandardErrorDialogState()
    const [showExtendBookingConfirmModal, setShowExtendBookingConfirmModal] = useState(false)
    const [showEndBookingConfirmModal, setShowEndBookingConfirmModal] = useState(false)
    const [showReceiptModal, setShowReceiptModal] = useState(false)
    const [deletedOrderId, setDeletedOrderId] = useState<number | null>(null)
    const { lockedDialog, openLockedDialog, closeLockedDialog: closeLockedDialogState } = useLockedDialogState()

    const [showSuccessModal, setShowSuccessModal] = useState<{
        open: boolean
        type?: SuccessDialogType
        closeCallback?: () => void
        deletionData?: any
    } | null>(null)
    const [confirmationDialog, setConfirmationDialog] = useState<{
        open: boolean
        actionType: ActionType
    } | null>(null)

    const { data: orderDetails, isLoading } = useApiQuery(fetchOrderDetails, {
        queryName: UserWeekMenu.USER_MEETING_SERVICE_DETAILS,
        dependencies: {
            orderId: meeting.id,
        },
        enabled: (anchorEl === null ? Boolean(anchorEl) : menuOnApi) && meeting.id !== deletedOrderId,
        select: transformData,
        useErrorBoundary: true,
    })

    const { mutate: callEndBooking, isLoading: isEndingBooking } = useApiMutation(ordersEndBooking, {
        onSuccess: async () => {
            setShowEndBookingConfirmModal(false)
            setShowSuccessModal({ open: true, type: "endBooking", closeCallback: deleteCallback })
            await invalidateOrderQueries()
        },
    })

    const orderAction = orderDetails?.orderAction ?? meeting.orderAction
    const { classes, cx } = useStyles({ hasTopPadding: !topComponent && !additionalMenuItems?.length && !!orderAction })

    const editMeeting = () => {
        const { hasBooking, hasCatering } = orderDetails?.orderSummary ?? {}

        if (hasBooking && !hasCatering) {
            if (!orderAction?.updateBooking?.allow) {
                return openLockedDialog({
                    title:
                        orderAction?.updateBooking?.message?.title ||
                        l10n.getString("user-home-canteen-upcoming-orders-update"),
                    body:
                        orderAction?.updateBooking?.message?.body ||
                        l10n.getString("user-home-canteen-upcoming-orders-update-booking-restriction-message"),
                })
            }
        }

        if (hasCatering && !hasBooking) {
            if (!orderAction?.updateMeetingService?.allow) {
                return openLockedDialog({
                    title:
                        orderAction?.updateMeetingService?.message?.title ||
                        l10n.getString("user-home-canteen-upcoming-orders-update"),
                    body:
                        orderAction?.updateMeetingService?.message?.body ||
                        l10n.getString("user-home-canteen-upcoming-orders-update-meeting-restriction-message"),
                })
            }
        }

        setConfirmationDialog({
            open: true,
            actionType: "edit_meeting",
        })
    }

    const handleEditMeetingConfirm = () => {
        const { hasBooking, hasCatering } = orderDetails?.orderSummary ?? {}

        let startType: CreateOrderType = CreateOrderType.RESOURCE_BOOKING

        if (hasCatering && !hasBooking) {
            startType = CreateOrderType.MEETING_SERVICE
        }

        openDialog({
            orderId: meeting.id,
            mode: CreateOrderDialogMode.EDIT,
            activeOrderTab: startType,
        })
    }

    const extendBooking = () => {
        if (orderAction?.extendBooking.allow) {
            setShowExtendBookingConfirmModal(true)
        } else {
            openLockedDialog({
                title:
                    orderAction?.extendBooking.message?.title ||
                    l10n.getString("user-home-canteen-upcoming-orders-extend-booking-item"),
                body:
                    orderAction?.extendBooking.message?.body ||
                    l10n.getString("user-home-canteen-upcoming-orders-extend-booking-restriction-message"),
            })
        }
    }

    // A booking that is already running cannot be deleted any more; cancelling it means ending it now.
    const endStartedBooking = () => {
        if (orderAction?.endBooking?.allow) return setShowEndBookingConfirmModal(true)

        return openLockedDialog({
            title:
                orderAction?.endBooking?.message?.title ||
                l10n.getString("user-home-canteen-upcoming-orders-cancel-order-item"),
            body:
                orderAction?.endBooking?.message?.body ||
                l10n.getString("user-home-canteen-upcoming-orders-end-booking-restriction-message"),
        })
    }

    // the booking ends at the moment of cancelling; an end time in the past makes the backend drop the order
    const handleEndBookingConfirm = () => {
        callEndBooking({
            orderId: meeting.id,
            endDateTime: dateTimeFactory.now().toISO() ?? "",
        })
    }

    const duplicateMeetingService = () => {
        if (!orderAction?.duplicateMeetingService?.allow) {
            return openLockedDialog({
                title:
                    orderAction?.duplicateMeetingService?.message?.title ||
                    l10n.getString("user-home-canteen-upcoming-orders-duplicate-meeting-service-item-meeting-service"),
                body:
                    orderAction?.duplicateMeetingService?.message?.body ||
                    l10n.getString("user-home-canteen-upcoming-orders-duplicate-meeting-service-restriction-message"),
            })
        }

        setConfirmationDialog({
            open: true,
            actionType: "duplicate_meeting_service",
        })
    }

    const handleDuplicateMeetingServiceConfirm = () => {
        openDialog({
            orderId: meeting.id,
            mode: CreateOrderDialogMode.DUPLICATE,
            activeOrderTab: CreateOrderType.MEETING_SERVICE,
        })
    }

    const deleteCallback = () => {
        handleMenuClose()
        onOrderDeleted?.()
        queryClient.invalidateQueries({
            queryKey: [UserWeekMenu.MEETING_SERVICE_LIST],
        })
    }

    const onDelete = () => {
        // not every surface fetches the details (menuOnApi), so fall back to the list item
        const order = orderDetails ?? meeting
        const now = dateTimeFactory.now()
        const start = dateTimeFactory.fromApiOrUndefined(order.meetingDetails.startDateTime)
        const end = dateTimeFactory.fromApiOrUndefined(order.meetingDetails.endDateTime)
        const hasStarted = start !== undefined && start.isValid && start <= now
        const isOver = end !== undefined && end.isValid && now >= end

        if (order.orderSummary.hasBooking && hasStarted) {
            // the backend's message for this case is not localized and talks about a started meeting
            if (isOver) {
                return openLockedDialog({
                    title: l10n.getString("user-home-canteen-upcoming-orders-cancel-order-item"),
                    body: l10n.getString("user-home-canteen-upcoming-orders-cancel-ended-booking-message"),
                })
            }

            return endStartedBooking()
        }

        const { hasBooking, hasCatering } = orderDetails?.orderSummary ?? {}

        if (hasBooking && !hasCatering) {
            if (!orderAction?.deleteBooking.allow) {
                return openLockedDialog({
                    title:
                        orderAction?.deleteBooking.message?.title ||
                        l10n.getString("user-home-canteen-upcoming-orders-delete-order"),
                    body:
                        orderAction?.deleteBooking.message?.body ||
                        l10n.getString("user-home-canteen-upcoming-orders-delete-order-restriction-message"),
                })
            }
            return setShowDeleteModal(true)
        }

        if (hasCatering && !hasBooking) {
            if (!orderAction?.deleteMeetingService.allow) {
                return openLockedDialog({
                    title:
                        orderAction?.deleteMeetingService.message?.title ||
                        l10n.getString("user-home-canteen-upcoming-orders-delete-order"),
                    body:
                        orderAction?.deleteMeetingService.message?.body ||
                        l10n.getString("user-home-canteen-upcoming-orders-delete-order-restriction-message"),
                })
            }
            return setShowDeleteModal(true)
        }

        return setShowDeleteModal(true)
    }

    const handleShowReceipt = () => setShowReceiptModal(true)
    const handleCloseReceipt = () => {
        setShowReceiptModal(false)
        handleMenuClose()
    }

    const closeLockedDialog = () => {
        closeLockedDialogState()
        handleMenuClose()
    }

    const invalidateOrderQueries = useCallback(async () => {
        // The order details drive the cancel/end decision, so they must not stay cached after an update.
        await queryClient.invalidateQueries({
            queryKey: [UserWeekMenu.USER_MEETING_SERVICE_DETAILS],
        })
        await queryClient.invalidateQueries({
            queryKey: [UserWeekMenu.MEETING_SERVICE_LIST],
            refetchType: "all",
        })
        // Invalidate queries after deletion
        await queryClient.invalidateQueries({
            queryKey: [UserWeekMenu.USER_MEETING_ORDER_FREE_BUSY_SCHEDULE],
            refetchType: "all", // adding because react query does not refetch query.
        })
        // Invalidate calendar events to refresh the calendar after deletion
        await queryClient.invalidateQueries({
            queryKey: [UserDetails.USER_CALENDAR_EVENTS],
            refetchType: "all",
        })
        // Invalidate upcoming orders table to refresh after deletion
        await queryClient.invalidateQueries({
            queryKey: [UpcomingOrdersMeetingService.UPCOMING_ORDERS_MEETING_SERVICE],
            refetchType: "all",
        })
        // Refresh the service-center home meetings list when this menu is used from that drawer (no-op elsewhere).
        await queryClient.invalidateQueries({
            queryKey: [ServiceCenterHomeQuery.SERVICE_CENTER_MEETINGS],
            refetchType: "all",
        })
        // Refresh the service-center upcoming meetings list when this menu is used from that page (no-op elsewhere).
        await queryClient.invalidateQueries({
            queryKey: [ServiceCenterUpcomingMeetingsQuery.UPCOMING_MEETINGS],
            refetchType: "all",
        })
    }, [queryClient])

    const handleSuccessDialogClose = useCallback(async () => {
        showSuccessModal?.closeCallback?.()
        handleMenuClose()

        await invalidateOrderQueries()

        setDeletedOrderId(null)

        // Show snackbar if there's a service response message
        if (showSuccessModal?.deletionData?.serviceResponse?.displayMessage) {
            enqueueSnackbar({
                variant: "success",
                message: showSuccessModal.deletionData.serviceResponse.displayMessage,
            })
        }

        setShowSuccessModal({ open: false })
    }, [showSuccessModal, handleMenuClose, invalidateOrderQueries, enqueueSnackbar])

    const handleErrorDialogClose = useCallback(async () => {
        closeErrorDialog()
        handleMenuClose()

        await invalidateOrderQueries()
    }, [closeErrorDialog, handleMenuClose, invalidateOrderQueries])

    const closeConfirmationDialog = () => {
        setConfirmationDialog(null)
        handleMenuClose()
    }

    const closeEndBookingConfirmModal = () => {
        setShowEndBookingConfirmModal(false)
        handleMenuClose()
    }

    const handleConfirmAction = () => {
        if (!confirmationDialog) return

        switch (confirmationDialog.actionType) {
            case "edit_meeting":
                handleEditMeetingConfirm()
                break
            case "duplicate_meeting_service":
                handleDuplicateMeetingServiceConfirm()
                break
        }

        closeConfirmationDialog()
    }

    const customMenuItems = additionalMenuItems?.map(({ key, label, onClick }) => (
        <MenuItem key={key} onClick={() => onClick(meeting.id)}>
            {label}
        </MenuItem>
    ))

    const deleteAction =
        Boolean(orderAction?.deleteBooking) || Boolean(orderAction?.deleteMeetingService)
            ? [
                  <Divider key={`divider-delete`} className={classes.topComponentDivider} />,
                  <LocalizedStrict key="cancel-order" id="user-home-canteen-upcoming-orders-cancel-order-item">
                      <MenuItem className={classes.deleteItem} onClick={onDelete}>
                          Cancel order
                      </MenuItem>
                  </LocalizedStrict>,
              ]
            : []

    const renderMenuItems = (orderAction?: MeetingOrderAction) => {
        if (!orderAction) return []
        return menuItemsConfig
            .reduce<React.ReactNode[]>(
                (acc, { key, element, dividerBefore }, index) => {
                    const isShowReceipt = key === "showReceipt"

                    if (isShowReceipt) {
                        acc.push(element)
                        return acc
                    }

                    const action = orderAction[key as keyof MeetingOrderAction]

                    if (key === "editMeeting") {
                        if (orderAction.updateBooking || orderAction.updateMeetingService) {
                            if (dividerBefore && acc.length > 0) acc.push(<Divider key={`divider-${index}`} />)
                            acc.push(element)
                        }
                        return acc
                    }

                    if (!action) return acc
                    if (dividerBefore && acc.length > 0) acc.push(<Divider key={`divider-${index}`} />)

                    acc.push(element)
                    return acc
                },
                [
                    topComponent,
                    Boolean(topComponent) && <Divider key={`divider-top`} className={classes.topComponentDivider} />,
                    customMenuItems,
                    <LocalizedStrict id="user-home-canteen-upcoming-orders-receipt-item">
                        <MenuItem onClick={handleShowReceipt}>Receipt</MenuItem>
                    </LocalizedStrict>,
                    ...(onChatOpen
                        ? [
                              <LocalizedStrict id="order-chat-menu-item">
                                  <MenuItem
                                      key="chat"
                                      onClick={() => {
                                          onChatOpen(meeting.id, meeting.meetingDetails.meetingName)
                                          handleMenuClose()
                                      }}
                                  >
                                      Order message
                                  </MenuItem>
                              </LocalizedStrict>,
                          ]
                        : []),
                ],
            )
            .concat(deleteAction)
    }

    const noActionsAvailable = [
        <LocalizedStrict
            id="user-home-canteen-news-category-no-actions"
            vars={{
                orderId: meeting.id.toString(),
            }}
        >
            <Box className={classes.noActionsMenuItem}>No actions available for order #{meeting.id.toString()}</Box>
        </LocalizedStrict>,
    ]

    const menuItemsConfig: MenuItemConfig[] = [
        {
            key: "editMeeting",
            element: (
                <LocalizedStrict id="user-home-canteen-upcoming-orders-edit-item">
                    <MenuItem onClick={editMeeting}>Edit</MenuItem>
                </LocalizedStrict>
            ),
            dividerBefore: true,
        },
        {
            key: "extendBooking",
            element: (
                <LocalizedStrict id="user-home-canteen-upcoming-orders-extend-booking-item">
                    <MenuItem onClick={extendBooking}>Extend booking</MenuItem>
                </LocalizedStrict>
            ),
        },
        {
            key: "duplicateMeetingService",
            element: (
                <LocalizedEnum
                    base="user-home-canteen-upcoming-orders-duplicate-meeting-service-item"
                    enumValue={variant}
                >
                    <MenuItem onClick={duplicateMeetingService}>Duplicate</MenuItem>
                </LocalizedEnum>
            ),
        },
    ]

    const loadingSkeletons = (
        <>
            <Skeleton variant="rectangular" className={classes.skeletonCard} />
        </>
    )

    const menuItems =
        isLoading && menuOnApi ? loadingSkeletons : orderAction ? renderMenuItems(orderAction) : noActionsAvailable

    // The receipt and the action dialogs are portaled modals rendered as children of this menu.
    // On surfaces that mount the menu conditionally (e.g. the calendar's `{anchorEl && <MeetingCardMenu/>}`),
    // a click inside one of those modals counts as a click-away for the Popper and would call
    // `handleMenuClose`, unmounting the menu together with the open modal. Ignore click-away while a
    // child modal is open so it can only be closed through its own controls.
    const isChildModalOpen =
        showReceiptModal ||
        showDeleteModal ||
        showExtendBookingConfirmModal ||
        showEndBookingConfirmModal ||
        Boolean(showSuccessModal?.open) ||
        Boolean(confirmationDialog) ||
        showErrorDialog.open ||
        lockedDialog.open

    const handleClickAway = () => {
        if (isChildModalOpen) return

        handleMenuClose()
    }

    return (
        <>
            {ignoreMenuContainer ? (
                menuItems
            ) : (
                <Popper
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    placement="bottom-end"
                    transition
                    className={cx(classes.menu, classes.popper)}
                >
                    {({ TransitionProps }) => (
                        <Grow {...TransitionProps}>
                            <Paper elevation={8}>
                                <ClickAwayListener onClickAway={handleClickAway}>
                                    <MenuList onMouseEnter={onMenuMouseEnter} onMouseLeave={onMenuMouseLeave}>
                                        {menuItems}
                                    </MenuList>
                                </ClickAwayListener>
                            </Paper>
                        </Grow>
                    )}
                </Popper>
            )}

            {showErrorDialog.open && (
                <StandardErrorDialog onClose={handleErrorDialogClose} dialogDetails={showErrorDialog} />
            )}

            {showDeleteModal && (
                <UserDeleteMeetingDialog
                    meeting={orderDetails ?? meeting}
                    open={showDeleteModal}
                    disabledDeleteMeetingService={orderAction?.deleteMeetingService?.message?.body}
                    disabledDeleteBooking={orderAction?.deleteBooking?.message?.body}
                    onSuccess={(data) => {
                        setShowDeleteModal(false)
                        setDeletedOrderId(meeting.id)
                        const { hasCatering, hasBooking } = orderDetails?.orderSummary ?? meeting.orderSummary ?? {}
                        const isMeetingService = hasCatering && !hasBooking
                        setShowSuccessModal({
                            open: true,
                            type: isMeetingService ? "deleteOrder" : "deleteBooking",
                            closeCallback: deleteCallback,
                            deletionData: data,
                        })
                    }}
                    onError={(error) => {
                        setShowDeleteModal(false)
                        openErrorDialog(error)
                    }}
                    onClose={() => {
                        handleMenuClose()
                        setShowDeleteModal(false)
                    }}
                />
            )}

            {lockedDialog.open && <LockedDialog dialogDetails={lockedDialog} onClose={closeLockedDialog} />}

            {/* same layout as the delete confirmation, since both sit behind the one "Cancel order" action */}
            {showEndBookingConfirmModal && (
                <LocalizedStrict
                    id="user-home-canteen-upcoming-orders-cancel-started-modal"
                    attrs={{ title: true, message: true, buttonLabelSubmit: true, buttonLabelCancel: true }}
                    vars={{ orderId: meeting.id.toString(), meetingName: meeting.meetingDetails.meetingName }}
                >
                    <ConfirmModal
                        open={showEndBookingConfirmModal}
                        onClose={closeEndBookingConfirmModal}
                        autoCloseAfterSubmit={false}
                        hideCloseIcon={false}
                        title="Cancel order"
                        message={`The meeting #${meeting.id}, ${meeting.meetingDetails.meetingName} has already started, so the booking will be ended now instead of deleted. Do you want to continue?`}
                        buttonLabelSubmit="Cancel order"
                        buttonLabelCancel="Back"
                        buttonSubmitLoading={isEndingBooking}
                        handleClick={handleEndBookingConfirm}
                    />
                </LocalizedStrict>
            )}

            {confirmationDialog && (
                <ActionConfirmationDialog
                    open={confirmationDialog.open}
                    onClose={closeConfirmationDialog}
                    onConfirm={handleConfirmAction}
                    actionType={confirmationDialog.actionType}
                    meeting={orderDetails ?? meeting}
                />
            )}

            {showExtendBookingConfirmModal && (
                <StandardDialog
                    open={showExtendBookingConfirmModal}
                    onClose={() => {
                        handleMenuClose()
                        setShowExtendBookingConfirmModal(false)
                    }}
                    maxWidthProp="sm"
                    dialogTitle={
                        <LocalizedStrict id="user-home-canteen-upcoming-orders-extend-modal-title">
                            <>Extend booking</>
                        </LocalizedStrict>
                    }
                >
                    <Grid
                        container
                        justifyContent="center"
                        alignItems="center"
                        direction="column"
                        wrap="nowrap"
                        className={classes.updateModalContainer}
                    >
                        <ExtendBookingContent
                            meeting={meeting}
                            onConfirm={() => {
                                setShowSuccessModal({ open: true, type: "extendBooking" })
                                setShowExtendBookingConfirmModal(false)
                            }}
                        />
                    </Grid>
                </StandardDialog>
            )}

            {showSuccessModal?.open && (
                <StandardSuccessDialog
                    dialogType={showSuccessModal?.type}
                    open={showSuccessModal.open || true}
                    onClose={handleSuccessDialogClose}
                />
            )}

            {showReceiptModal && (
                <UserMeetingsReceipt
                    updateEdge={classes.hideEdge}
                    open={showReceiptModal}
                    id={meeting.id}
                    meetings={orders ?? meetings}
                    onClose={handleCloseReceipt}
                />
            )}
        </>
    )
}

export const MeetingCardMenu = (props: MeetingMenuCardProps) => (
    <ConfigErrorBoundary onClose={props?.handleMenuClose}>
        <MeetingCard {...props} />
    </ConfigErrorBoundary>
)
